import React from "react"

// Libraries
import PropTypes from "prop-types"
import styled from "styled-components"
import { transparentize } from "polished"
import { Link } from "gatsby-plugin-react-i18next"
import { useTranslation } from "react-i18next"
import { Trans } from "react-i18next"

// Utils
import { colors } from "utils/variables/"
import breakpoint from "utils/breakpoints/"

// Components
import ButtonWithIcon from "components/button-with-icon/"
import Lottie from "components/lottie-observed/"

// Icons
import IconCaretRound from "assets/icons/icon-arrow-right.inline.svg"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

const StyledCaseStudyCard = styled(Link)`
  height: 100%;
  width: 100%;

  position: relative;
  display: flex;
  flex-wrap: wrap;

  &:focus,
  &:focus-visible,
  &:focus-within {
    box-shadow: 0 0 0 8px ${transparentize(0.7, colors.cream)};
  }

  &:hover {
    .card__content {
      background-color: ${colors.midnight};
      color: ${colors.white};

      .card__link {
        color: ${colors.supernova};
      }
    }
  }

  .card__image {
    img {
      display: flex;
      background-color: ${colors.midnight};
      ${breakpoint.small`
        height: 364px;
      `}
    }
    svg {
      display: flex;
    }
  }

  .card__image,
  .card__content {
    ${breakpoint.small`
      width: 50%;
    `}

    > div {
      width: 100%;
    }

    .title {
      max-width: 400px;
    }
  }

  .card__content {
    display: flex;
    flex-wrap: wrap;
    padding: 32px 16px;
    transition: all 0.3s ease;

    ${breakpoint.small`
      padding: 32px 32px 22px 32px;
    `}

    .card__link {
      margin-top: 40px;
      align-self: flex-end;
    }
  }

  .badge {
    position: absolute;
    right: 16px;
    bottom: 30px;

    ${breakpoint.small`
      top: 34px;
      right: 40px;
      bottom: auto;
    `}
  }
`

const StyledCaseStudyList = styled(Link)`
  display: block;
  padding: 16px 0;
  margin-bottom: -16px;
  border-top: 1px solid ${colors.indiblue};
  transition: all 0.3s ease;

  ${breakpoint.medium`
    margin-bottom: -40px;
  `}

  &:hover,
  &:active {
    border-top: 1px solid ${colors.midnight};
  }
`

const CaseStudyCard = props => {
  const {
    slug,
    title,
    view,
    client,
    covers,
    imagen,
    size,
    year,
    new: isNew,
  } = props

  const { t } = useTranslation()

  if (view === "card") {
    return (
      <StyledCaseStudyCard to={`/work/${slug}/`} title={title}>
        <div className="card__image">
          {covers ? (
            covers[size] || covers.default
          ) : imagen.file.contentType === "application/json" ? (
            <div className="object">
              <Lottie options={imagen.file.url} path={imagen.file.url} loop />
            </div>
          ) : (
            <GatsbyImage
              image={getImage(imagen)}
              alt={title}
              width={464}
              height={420}
              quality={100}
            />
          )}
        </div>
        <div className="card__content bg--cream">
          <div>
            <h4 className="color--indiblue mb-3 text-uppercase font-weight--700">
              <Trans>{client}</Trans>
            </h4>
            <h3 className="title">
              <Trans>{title}</Trans>
            </h3>
          </div>

          <ButtonWithIcon className="card__link" text={t("View Case Study")} />
        </div>

        {isNew && (
          <span className="badge d-inline-flex bg--supernova color--midnight px-2 py-1">
            <h4 className="font-weight--700">
              <Trans>NEW!</Trans>
            </h4>
          </span>
        )}
      </StyledCaseStudyCard>
    )
  }

  return (
    <StyledCaseStudyList to={`/work/${slug}/`} title={title}>
      <div className="row justify-content-sm-between">
        <div className="col-3 col-sm-2 mb-3 mb-sm-0">
          <p className="paragraph--small text-uppercase">
            <Trans>{client}</Trans>
          </p>
        </div>
        <div className="col-3 col-sm-1 mb-3 mb-sm-0">
          <p className="paragraph--detail color--stone">
            <Trans>{year}</Trans>
          </p>
        </div>
        <div className="col-6 d-flex justify-content-end d-sm-none mb-3 mb-sm-0">
          <IconCaretRound />
        </div>
        <div className="col-12 col-sm-3">
          <p className="paragraph--detail">
            <Trans>{title}</Trans>
          </p>
        </div>
        <div className="d-none d-sm-block col-2 text-center">
          {isNew && (
            <span className="badge d-inline-flex bg--supernova color--midnight px-2 py-1">
              <h4 className="font-weight--700">
                <Trans>NEW!</Trans>
              </h4>
            </span>
          )}
        </div>
        <div className="d-none d-sm-block col-2">
          {covers ? (
            covers[size] || covers.default
          ) : imagen.file.contentType === "application/json" ? (
            <div className="object">
              <Lottie options={imagen.file.url} path={imagen.file.url} loop />
            </div>
          ) : (
            <GatsbyImage
              image={getImage(imagen)}
              alt={title}
              width={464}
              height={364}
              quality={100}
            />
          )}
        </div>
      </div>
    </StyledCaseStudyList>
  )
}

CaseStudyCard.propTypes = {
  slug: PropTypes.string.isRequired,
  view: PropTypes.oneOf(["card", "list"]),
  client: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  new: PropTypes.bool,
  size: PropTypes.oneOf(["default", "large"]),
  imagen: PropTypes.shape({}),
}

CaseStudyCard.defaultProps = {
  view: "card",
  new: false,
  size: "default",
}

export default CaseStudyCard
